// PermissionGate / ReadOnlyBanner — UI primitives for the view/edit RBAC
// model.
//
// Pattern across the portal:
//   - Pages always *render*; the AccessGuard already filtered out users
//     who shouldn't be there at all.
//   - Write actions (Save, Submit, Delete, Add, Approve, etc.) wrap their
//     button + the underlying handler in <PermissionGate>. View-only users
//     either see no button at all (default) or see a disabled-looking
//     button with a tooltip explaining why they can't act.
//   - Top of every page that has any write surface, render <ReadOnlyBanner>.
//     The banner is null for edit-grants — invisible UX cost. For view
//     grants it sits at the top so the user immediately understands why
//     buttons are missing.
//
// Resolution:
//   The gate checks the URL of the page it's on (useLocation) and asks
//   useUserRoles().canEditSurface(path). Pages where the URL alone isn't
//   enough (e.g. /forms hub branches by sub-path) can pass an explicit
//   `surface` prop.
//
//   A surface grant answers "may this role edit anything on this screen"; it
//   does NOT answer "may this role perform this particular action". Where the
//   server gates an action on a named capability, pass `capability` too (#1070,
//   epic #1069 phase 1a): the gate then requires BOTH the surface edit grant
//   and staff.has_my_capability(capability), so the button disappears for the
//   same users the MCP verb would refuse instead of offering them an action
//   that fails on submit. The server is always the real gate — this only keeps
//   the UI honest about it.
//
//   The capability check is delegated to useHasCapability (one RPC per mount
//   per capability) and is therefore only mounted when `capability` is set:
//   the surface-only path must not pay for an RPC it never reads, and the
//   portal has well over a hundred surface-only PermissionGate call sites.

import type { ReactNode } from "react";
import { useLocation } from "react-router";
import { Lock } from "lucide-react";

import { useHasCapability } from "@/hooks/useHasCapability";
import { useUserRoles } from "@/hooks/useUserRoles";
import { cn } from "@/lib/cn";

export interface PermissionGateProps {
  /** URL prefix to check; defaults to the current location.pathname. */
  surface?: string;
  /**
   * Named capability the gated action also requires, e.g. `"qa.decide"`.
   * When set, the surface edit grant is necessary but NOT sufficient —
   * `staff.has_my_capability(capability)` must also be true. Use the exact
   * capability the server-side verb passes to `requireCapability`.
   */
  capability?: string;
  /**
   * What to render when the user does NOT have edit access.
   *   - "hide"  (default): render nothing. Use for inline action buttons
   *     where a missing button is the clearest signal.
   *   - ReactNode: render this in place of children. Useful for "View
   *     only" badges that replace the action.
   */
  fallback?: "hide" | ReactNode;
  children: ReactNode;
}

/**
 * Render `children` only when the current user has 'edit' on `surface` — and,
 * when `capability` is given, also holds that capability.
 * Everyone else sees `fallback` (or nothing).
 */
export function PermissionGate({
  surface,
  capability,
  fallback = "hide",
  children,
}: PermissionGateProps) {
  // Branching on the presence of `capability` (a static per-call-site prop,
  // never toggled at runtime) rather than calling useHasCapability
  // unconditionally with a placeholder: the hook fires an RPC on mount, and
  // the surface-only call sites must not trigger one.
  if (capability) {
    return (
      <CapabilityGate surface={surface} capability={capability} fallback={fallback}>
        {children}
      </CapabilityGate>
    );
  }
  return (
    <SurfaceGate surface={surface} fallback={fallback}>
      {children}
    </SurfaceGate>
  );
}

function SurfaceGate({
  surface,
  fallback,
  children,
}: Omit<PermissionGateProps, "capability">) {
  const location = useLocation();
  const { loading, canEditSurface } = useUserRoles();

  // While roles are loading, don't render the action — better than briefly
  // letting a view-only user click a button before the gate kicks in.
  if (loading) return null;

  const path = surface ?? location.pathname;
  if (canEditSurface(path)) return <>{children}</>;
  if (fallback === "hide") return null;
  return <>{fallback}</>;
}

function CapabilityGate({
  surface,
  capability,
  fallback,
  children,
}: PermissionGateProps & { capability: string }) {
  const location = useLocation();
  const { loading: rolesLoading, canEditSurface } = useUserRoles();
  const { hasCapability, loading: capLoading } = useHasCapability(capability);

  // Same fail-closed-while-loading posture as the surface-only path.
  if (rolesLoading || capLoading) return null;

  const path = surface ?? location.pathname;
  if (canEditSurface(path) && hasCapability) return <>{children}</>;
  if (fallback === "hide") return null;
  return <>{fallback}</>;
}

/**
 * Banner pinned to the top of edit-capable pages. Renders nothing when the
 * user has edit access. For view-only grants it shows a small notice
 * explaining the page is read-only and points them at the admin to upgrade.
 */
export function ReadOnlyBanner({ surface }: { surface?: string }) {
  const location = useLocation();
  const { loading, levelForSurface } = useUserRoles();

  if (loading) return null;
  const level = levelForSurface(surface ?? location.pathname);
  if (level !== "view") return null;

  return (
    <div
      role="status"
      className={cn(
        "mb-4 flex items-start gap-2 rounded-md border border-amber-200",
        "bg-amber-50 p-3 text-xs text-amber-900",
      )}
    >
      <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
      <div>
        <p className="font-medium">Read-only access</p>
        <p>
          You can view this page but can&apos;t make changes. Ask an admin
          to upgrade your role from "view" to "edit" if you need to act
          here.
        </p>
      </div>
    </div>
  );
}

/**
 * Hook variant for places where conditional rendering inside JSX would be
 * awkward — e.g. computing initial form state, deciding whether to attach
 * an `onSubmit`, or feeding `disabled` into a third-party component.
 */
export function usePermissionForCurrentPage(): {
  loading: boolean;
  canEdit: boolean;
  level: "view" | "edit" | null;
} {
  const location = useLocation();
  const { loading, canEditSurface, levelForSurface } = useUserRoles();
  return {
    loading,
    canEdit: !loading && canEditSurface(location.pathname),
    level: loading ? null : levelForSurface(location.pathname),
  };
}
